import type { CSSProperties } from 'react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import Input from '@/components/Inputs/Input';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import useDropdownPos from '@/hooks/pure/useDropdownPos';
import useKeymap from '@/hooks/pure/useKeymap';
import useDropdownStore from '@/stores/pure/DropdownStore';
import zIndex from '@/styles/zIndex';

import { CenterFlex, Flex, VFlex } from '../base';
import Button from '../Button/Button';
import FadeDiv from '../Common/FadeDiv';
import ScrollView from '../Common/ScrollView';
import Spinner from '../Common/Spinner';
import Transition from '../Common/Transition';
import { MainText } from '../Typography/NamedTypo';
import SelectPortal from './SelectPortal';
import { Option } from './styled';
import type { ModalOptions, TriggerPos } from './types';

export type SearchSelectDataT = {
  alias: string;
  name: string;
  node: ReactNode;
};

export type FilterInfo = {
  queryKey: string;
  queryValue: string;
  valueName: string;
};

interface Props<T> {
  placeholder: string;
  dataList: SearchSelectDataT[];
  dataListInFeedback?: any[];
  style?: any;
  name: string;
  shortcut?: string;
  filter?: FilterInfo;
  clearable?: boolean;
  clearText?: ReactNode;
  onClear?: () => void;
  createNewItem?: (itemKey: string) => Promise<T>;
  toggleItem: (item: T | null) => void;
  triggerEl?: HTMLDivElement | null;
  triggerPos?: TriggerPos;
  modalWidth: CSSProperties['width'];
  async?: boolean;
  fetchFn: (
    prev?: T[],
    currPage?: number,
    targetPage?: number,
    searchValue?: string,
    accumulate?: boolean,
    queryObj?: QueryObj,
  ) => Promise<{
    page: number;
    hasNextPage: boolean;
    list: T[];
  }>;
  parser: (originalList: T[]) => SearchSelectDataT[];
  modalOptions?: ModalOptions;
}

type DropdownProps<T extends { alias: string }> = Props<T> & {
  close: () => void;
  dropdownRef: (dropdownEl: HTMLDivElement) => void;
};

const SearchSelectModal = <
  T extends {
    alias: string;
  },
>(
  props: Props<T>,
) => {
  const { currDropdown, setCurrDropdown, shortcutEnabled } = useDropdownStore(
    ({ currDropdown, setCurrDropdown, shortcutEnabled }) => ({
      currDropdown,
      setCurrDropdown,
      shortcutEnabled,
    }),
  );

  useKeymap(props.shortcut, () => {
    if (dropdownOpen) {
      setCurrDropdown(null);
    } else {
      shortcutEnabled && setCurrDropdown(props.name);
    }
  });

  const dropdownOpen = useMemo(() => {
    return currDropdown === props.name;
  }, [currDropdown, props.name]);

  const { setDropdownRef, dropdownPos } = useDropdownPos(dropdownOpen, {
    el: props.triggerEl,
    pos: props.triggerPos,
    options: props.modalOptions,
  });

  return (
    <SelectPortal
      status={dropdownOpen}
      close={() => setCurrDropdown(null)}
      style={dropdownPos}
    >
      <Dropdown
        {...props}
        close={() => setCurrDropdown(null)}
        dropdownRef={setDropdownRef}
      />
    </SelectPortal>
  );
};

type States<T> = {
  filter?: FilterInfo;
  originalData: T[];
  page: number;
  hasNextPage: boolean;
  searchValue: string;
  loading: boolean;
};

const Dropdown = <T extends { alias: string }>({
  placeholder,
  dataList,
  dataListInFeedback,
  async,
  filter,
  clearable,
  clearText,
  onClear,
  createNewItem,
  toggleItem,
  close,
  fetchFn,
  parser,
  modalWidth,
  dropdownRef,
}: DropdownProps<T>) => {
    const [lock, setLock] = useState(false);
    const [state, setState] = useState<States<T>>({
      filter: filter,
      originalData: [],
      page: 0,
      hasNextPage: false,
      searchValue: '',
      loading: false,
    });
    const color = useColor();
    const [searchValue, setSearchValue] = useState<string>('');

    const onClickExistingItem = (itemAlias: string) => {
      const item = state.originalData.find((data) => data.alias === itemAlias);
      if (toggleItem && item) toggleItem(item);
    };

    const onClickNewItem = async () => {
      if (createNewItem && toggleItem && !lock) {
        setLock(true);
        const item = await createNewItem(searchValue);
        item && toggleItem(item);
        setSearchValue('');
        setLock(false);
      }
    };

    const newKeywordCondition = useMemo(() => {
      if (searchValue === '') return false;
      const hasSame =
        dataList.findIndex((data) => {
          return data.name === searchValue;
        }) !== -1;
      if (hasSame) return false;

      return true;
    }, [searchValue, dataList]);

    const fetchData = useCallback(
      async (
        prev?: T[],
        targetPage?: number,
        searchValue?: string,
        accumulate?: boolean,
      ) => {
        if (!fetchFn) return [];
        setState((prevState) => ({
          ...prevState,
          loading: true,
        }));
        const res = await fetchFn(
          prev,
          state.page,
          targetPage,
          searchValue,
          accumulate,
          state.filter?.queryKey && state.filter?.queryValue
            ? {
                [state.filter.queryKey]: state.filter.queryValue,
              }
            : {},
        );

        const originalList =
          accumulate && prev ? [...prev, ...res.list] : res.list;

        setState((prevState) => ({
          ...prevState,
          originalData: originalList,
          hasNextPage: res.hasNextPage,
          loading: false,
        }));
      },
      [fetchFn, state.filter?.queryKey, state.filter?.queryValue, state.page],
    );

    useEffect(() => {
      if (async) {
        fetchData?.();
      }
    }, [async, fetchData]);

    const options = useMemo(() => {
      if (!async) return dataList;
      return parser(state.originalData);
    }, [async, dataList, parser, state.originalData]);

    const [focused, setFocused] = useState<string | null>(null);

    const focusNextItem = () => {
      if (!focused && options.length > 0) {
        setFocused(options[0].alias);
        return;
      }
      const curr = options.findIndex((keyword) => keyword.alias === focused);
      if (curr === options.length - 1) return;
      setFocused(options[curr + 1].alias);
    };

    const focusPrevItem = () => {
      if (!focused && options.length > 0) return;
      const curr = options.findIndex((keyword) => keyword.alias === focused);
      if (curr === 0) return;
      setFocused(options[curr - 1].alias);
    };

    const onKeyEnter = () => {
      if (!focused) return;
      const item = state.originalData.find((data) => data.alias === focused);
      item && toggleItem(item);
      close();
    };

    useKeymap('ArrowUp', focusPrevItem);

    useKeymap('ArrowDown', focusNextItem);

    useKeymap('Enter', onKeyEnter);

    const debounced = useDebouncedCallback(async (value: string) => {
      fetchData([], 0, value, false);
    }, 300);

    const onFilterClear = () => {
      setState((prevState) => ({
        ...prevState,
        filter: undefined,
      }));
    };

    return (
      <Wrapper
        ref={dropdownRef}
        onClick={(e) => e.stopPropagation()}
        style={{ width: modalWidth }}
      >
        <Input
          initFocus
          value={searchValue}
          sizeType="m"
          placeholder={placeholder}
          onChange={(e) => {
            if (async) {
              debounced(e.currentTarget.value);
            }
            setSearchValue(e.currentTarget.value);
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.code === 'ArrowUp') {
              focusPrevItem();
              e.currentTarget.blur();
            }
            if (e.code === 'ArrowDown') {
              focusNextItem();
              e.currentTarget.blur();
            }

            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              if (newKeywordCondition) {
                onClickNewItem();
              } else {
                onKeyEnter();
              }
            }
            if (e.code === 'Escape') {
              close();
            }
          }}
        />
        <ScrollView style={{ height: 300 }}>
          <FadeDiv status={true}>
            {state.filter && state.filter.valueName && (
              <Flex g="6px" a="center" style={{ padding: 12 }}>
                <MainText dim>Filtered by "{state.filter.valueName}"</MainText>
                <Typo
                  type="label4"
                  color={color.gray500}
                  style={{ cursor: 'pointer' }}
                  onClick={onFilterClear}
                >
                  Clear
                </Typo>
              </Flex>
            )}

            <Flex d="column" className="item-list">
              {createNewItem && newKeywordCondition && (
                <NewItem
                  d="row"
                  a="center"
                  g="5px"
                  j="space-between"
                  disabled={lock}
                  onClick={onClickNewItem}
                >
                  <Typo
                    style={{ userSelect: 'none', flex: 1 }}
                    weight="r"
                    type="body3"
                    color={color.gray700}
                    multilineEllipsis={1}
                  >
                    {searchValue}
                  </Typo>
                  <Typo
                    style={{ userSelect: 'none' }}
                    weight="r"
                    type="body3"
                    color={color.primary100}
                  >
                    {' new'}
                  </Typo>
                </NewItem>
              )}
              {clearable && (
                <Option
                  focused={dataListInFeedback?.length === 0}
                  onClick={() => onClear?.()}
                >
                  {clearText}
                </Option>
              )}
              {options.length > 0 &&
                options.map((data) => {
                  const isSelected = dataListInFeedback
                    ? dataListInFeedback.includes(data.alias)
                    : false;
                  return (
                    <Option
                      key={data.alias}
                      focused={isSelected}
                      onClick={() => onClickExistingItem(data.alias)}
                    >
                      {data.node}
                    </Option>
                  );
                })}
            </Flex>
            <Transition status={state.loading}>
              <FadeDiv status={true}>
                <CenterFlex style={{ height: 48 }}>
                  <Spinner />
                </CenterFlex>
              </FadeDiv>
            </Transition>

            <Flex d="column" className="item-list">
              {!state.loading && state.hasNextPage && (
                <Button
                  variant="secondary"
                  disabled={lock}
                  onClick={() => {
                    fetchData(
                      state.originalData,
                      state.page + 1,
                      searchValue,
                      true,
                    );
                  }}
                >
                  <Typo type="label3" color={color.gray500}>
                    Load more
                  </Typo>
                </Button>
              )}
            </Flex>
          </FadeDiv>
        </ScrollView>
      </Wrapper>
    );
};

const Wrapper = styled(VFlex)`
  width: 100%;
  z-index: ${zIndex.modal};
  padding: 8px;
  gap: 8px;
  cursor: default;
`;

const NewItem = styled(Flex)`
  padding: 4px 16px;
  cursor: pointer;
  border-radius: 2px;
  background-color: ${(props) => props.theme.colors.primaryAlpha(0.2)};
  opacity: ${(props) => props.disabled && 0.5};
`;

export default SearchSelectModal;
